import { useEffect, useState, useSyncExternalStore, useCallback } from 'react';
import { Star } from 'lucide-react';
import {
  subscribe, getSnapshot, hydrate, isStarred as storeIsStarred,
  star as storeStar, unstar as storeUnstar, listForTicker as storeListForTicker,
} from '@/lib/starredFilingsStore';

/**
 * React hook bridging the starred-filings store to component state.
 * Uses useSyncExternalStore so any mutation anywhere in the app
 * re-renders this consumer (the Watchlist strip stays in sync with
 * the Star button inside the Stock Modal, and vice versa).
 */
export function useStarredFilings() {
  // Trigger hydrate once on first mount — store dedupes concurrent
  // hydrate calls so spawning multiple Star buttons is cheap.
  useEffect(() => { hydrate(); }, []);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useIsStarred(accession) {
  useStarredFilings();
  return storeIsStarred(accession);
}

export function useStarredForTicker(ticker) {
  useStarredFilings();
  return storeListForTicker(ticker);
}

/**
 * StarFilingButton — Round 92o++++++++N++++++++++ (May 2026).
 *
 * Sits in front of 10-K section headers and 8-K row titles inside
 * the 10K/8K/Risks tab. Filled gold when starred, hollow grey when
 * not. Click toggles the state with an optimistic flip + auto-add
 * the parent ticker to the user's watchlist (backend side-effect).
 *
 * Props match the star payload shape so callers can pass a single
 * object describing the filing being starred.
 */
export default function StarFilingButton({
  ticker,
  accession,
  formType,
  section,
  filingDate,
  filingUrl,
  itemCodes,
  title,
  snippet,
  size = 'sm', // 'sm' | 'md'
  className = '',
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const starred = useIsStarred(accession);
  const onClick = useCallback(async (e) => {
    e.stopPropagation();
    if (!accession || !ticker || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (starred) {
        await storeUnstar(accession);
      } else {
        await storeStar({
          ticker,
          accession,
          form_type: formType,
          section,
          filing_date: filingDate,
          filing_url: filingUrl,
          item_codes: itemCodes || [],
          title,
          snippet,
        });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Star failed');
    } finally {
      setBusy(false);
    }
  }, [accession, ticker, formType, section, filingDate, filingUrl, itemCodes, title, snippet, starred, busy]);

  const dim = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || !accession}
      title={starred ? `Un-star — remove from your Watchlist filings`
                     : `Star — pin to your Watchlist for ${ticker}`}
      className={`inline-flex items-center justify-center rounded transition-colors ${
        busy ? 'opacity-60' : ''
      } ${className}`}
      data-testid={`star-filing-${accession || 'unknown'}`}
      aria-pressed={starred}
    >
      <Star
        className={`${dim} ${
          starred
            ? 'fill-amber-400 stroke-amber-500'
            : 'fill-none stroke-muted-foreground hover:stroke-amber-500'
        } transition-colors`}
      />
      {error && (
        <span className="ml-1 text-[9px] text-rose-500" title={error}>!</span>
      )}
    </button>
  );
}
